import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Paperclip, Loader2, CheckCircle2 } from 'lucide-react';
import { api } from '../../api/client';
import type { CheckoutPageData } from '@pay/shared';
import { OwnerHeader } from '../../components/OwnerHeader';
import { CustomerFooter } from '../../components/CustomerFooter';
import { lastVippsSdk, monterVippsKnapp, type VippsKnapp } from '../../lib/vippsWidget';

function formatAmount(ore: number) {
  return (ore / 100).toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** «laster» mens Vipps' skript hentes, «vipps» når den godkjente knappen er montert, «reserve» om skriptet ikke kom. */
type Knapp = 'laster' | 'vipps' | 'reserve';

export default function Checkout() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<CheckoutPageData | null>(null);
  const [phone, setPhone] = useState('');
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [knapp, setKnapp] = useState<Knapp>('laster');
  // Vipps-knappen lages én gang; trykket må lese nummeret slik det står i feltet da.
  const phoneRef = useRef('');
  useEffect(() => {
    phoneRef.current = phone;
  }, [phone]);

  function load() {
    if (!token) return;
    api
      .get<CheckoutPageData>(`/api/checkout/${token}`)
      .then((d) => {
        setData(d);
        setPhone(d.customer.phone ?? '');
      })
      .catch((e) => setError(e.message ?? 'Lenken er ugyldig eller utløpt.'));
  }
  useEffect(load, [token]);

  /** Oppretter betalingen hos Vipps og gir adressen til Vipps' betalingsside. */
  async function startBetaling(): Promise<string> {
    setStarting(true);
    setError(null);
    try {
      const r = await api.post<{ redirect_url: string }>(`/api/checkout/${token}/start`, { phone: phoneRef.current });
      return r.redirect_url;
    } catch (e) {
      setError((e as { message?: string }).message ?? 'Kunne ikke starte betalingen');
      throw e;
    } finally {
      setStarting(false);
    }
  }

  // Den godkjente Vipps-knappen (Widget SDK). På PC åpnes betalingen i en dialog, på mobil går siden
  // rett til Vipps; retur-adressen tar kunden tilbake hit i begge tilfeller.
  const kanBetale = data?.status === 'PENDING';
  useEffect(() => {
    if (!kanBetale || !token) return;
    let avbrutt = false;
    let montert: VippsKnapp | null = null;
    lastVippsSdk()
      .then((sdk) => {
        if (avbrutt) return;
        montert = monterVippsKnapp(sdk, '#vipps-betal', startBetaling);
        setKnapp('vipps');
      })
      .catch(() => {
        if (!avbrutt) setKnapp('reserve');
      });
    return () => {
      avbrutt = true;
      montert?.unmount();
    };
    // startBetaling leser token og telefonnummer når kunden trykker; knappen skal ikke lages på nytt.
  }, [kanBetale, token]);

  /** Reserve når Vipps' skript ikke lastet (blokkert eller nede): samme flyt, vanlig videresending. */
  async function payUtenKnapp() {
    try {
      window.location.href = await startBetaling();
    } catch {
      /* feilen vises over knappen */
    }
  }

  if (error && !data) {
    return (
      <div className="min-h-full flex flex-col items-center justify-center bg-slate-100 p-6">
        <div className="bg-white p-8 rounded-lg shadow max-w-md text-center">
          <div className="text-lg font-semibold mb-2">Beklager</div>
          <div className="text-slate-600">{error}</div>
        </div>
        <CustomerFooter />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  if (data.status === 'CAPTURED') {
    return (
      <div className="min-h-full bg-slate-100">
        <OwnerHeader owner={data.owner} />
        <main className="max-w-2xl mx-auto p-6">
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <CheckCircle2 size={56} className="mx-auto text-green-600 mb-4" />
            <h1 className="text-2xl font-semibold mb-2">Betaling fullført</h1>
            <p className="text-slate-600">
              {data.customer.email
                ? `Takk! Vi har sendt kvittering til ${data.customer.email}.`
                : 'Takk! Kvitteringen finner du i Vipps-appen.'}
            </p>
          </div>
          <CustomerFooter owner={data.owner} />
        </main>
      </div>
    );
  }

  // Kravet kan ikke betales lenger (avbrutt, utløpt eller allerede godkjent): si det rett ut.
  const stengt =
    data.status === 'ABORTED' ? 'Betalingskravet er trukket tilbake. Ta kontakt med ' + data.owner.name + ' om du har spørsmål.'
    : data.status === 'EXPIRED' ? 'Lenken er utløpt. Ta kontakt med ' + data.owner.name + ' for en ny.'
    : data.status === 'AUTHORIZED' ? 'Betalingen er godkjent og behandles nå.'
    : data.status === 'REFUNDED' || data.status === 'PARTIALLY_REFUNDED' ? 'Betalingen er refundert.'
    : data.status === 'FAILED' ? 'Betalingen kunne ikke gjennomføres. Ta kontakt med ' + data.owner.name + '.'
    : null;

  return (
    <div className="min-h-full bg-slate-100">
      <OwnerHeader owner={data.owner} />

      <main className="max-w-2xl mx-auto p-6 space-y-5">
        <section className="bg-white rounded-lg shadow p-6">
          <div className="text-slate-500 text-sm">Til betaling</div>
          <div className="text-4xl font-semibold mt-1 mb-1">{formatAmount(data.amount_ore)} kr</div>
          <div className="text-slate-700">{data.description}</div>

          <div className="mt-5 pt-5 border-t border-slate-200 text-sm space-y-1 text-slate-600">
            <div><span className="text-slate-500">Mottaker:</span> {data.owner.name}</div>
            <div><span className="text-slate-500">Til:</span> {data.customer.name}{data.customer.email ? ` (${data.customer.email})` : ''}</div>
          </div>
        </section>

        {data.attachments.length > 0 && (
          <section className="bg-white rounded-lg shadow p-6">
            <div className="font-medium mb-2 flex items-center gap-2">
              <Paperclip size={16} /> Vedlegg
            </div>
            <ul className="space-y-1">
              {data.attachments.map((a) => (
                <li key={a.id}>
                  <a href={a.download_url} target="_blank" rel="noopener" className="text-brand hover:underline text-sm">
                    {a.filename} <span className="text-slate-500">({Math.round(a.size_bytes / 1024)} kB)</span>
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        {stengt ? (
          <section className="bg-white rounded-lg shadow p-6 text-slate-700">{stengt}</section>
        ) : (
        <section className="bg-white rounded-lg shadow p-6 space-y-4">
          <label className="block">
            <span className="block text-sm font-medium text-slate-700 mb-1">Telefonnummer (for Vipps)</span>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Mobilnummer"
              inputMode="tel"
              className="w-full border border-slate-300 rounded px-3 py-2"
            />
          </label>

          {error && <div className="text-red-600 text-sm">{error}</div>}

          {/* Vipps' egen knapp monteres her av Widget SDK — React legger ikke noe inni. */}
          <div id="vipps-betal" />
          {knapp === 'laster' && (
            <div className="h-12 flex items-center justify-center text-slate-400" aria-label="Laster betalingsknappen">
              <Loader2 size={20} className="animate-spin" />
            </div>
          )}
          {knapp === 'reserve' && (
            <button
              onClick={payUtenKnapp}
              disabled={starting}
              className="w-full rounded py-3 font-semibold text-white bg-slate-800 hover:bg-slate-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {starting ? <Loader2 size={18} className="animate-spin" /> : null}
              Fortsett til betaling
            </button>
          )}
          {knapp === 'vipps' && starting && (
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
              <Loader2 size={14} className="animate-spin" /> Åpner Vipps …
            </div>
          )}

          <p className="text-xs text-slate-500 text-center">
            Ved å betale godtar du{' '}
            <a href={`/vilkar/${data.owner.id}`} target="_blank" rel="noopener" className="underline hover:text-slate-700">
              salgsvilkårene
            </a>{' '}
            til {data.owner.name}.
          </p>
        </section>
        )}

        <CustomerFooter owner={data.owner} />
      </main>
    </div>
  );
}
